import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { CommandColumnService, GridComponent, InfiniteScrollService, PageService, SelectionService, SortService, ToolbarService } from '@syncfusion/ej2-angular-grids';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { CustomAdaptor, gridActionBeginForFiltering, gridActionsForFiltering, handleClearFilter, updateFilterIcon, applyInfiniteGridTotalCount, bindInfiniteScrollPrefetch } from 'projects/service/src/app/shared/utils/grid-functions';
import { environment } from 'projects/service/src/environments/environment';
import { UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';

const BASE_URL = environment.apiUrl;

@Component({
    selector: 'app-config-advanced-search-for-serial-number',
    templateUrl: './config-advanced-search-for-serial-number.component.html',
    styleUrls: ['./config-advanced-search-for-serial-number.component.scss'],
    providers: [
        ToolbarService,
        SortService,
        PageService,
        InfiniteScrollService,
        SelectionService, CommandColumnService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ConfigAdvancedSearchForSerialNumberComponent implements OnInit {
  isExpanded: boolean = true;
  validationApplied: boolean = false;

  data: any;
  mainGrid: any;
  filterSettings: any;
  query: Query;
  selectedRowData: any;
  public infiniteScrollSettings: any = { enableCache: false, initialBlocks: 3, maxBlocks: 10 };
  totalRecords: number = 0;
  gridHeight: number = window.innerHeight - 350;

  userRoles;

  model: string = '';
  srNumber: string = '';

  @Input() gridType: any = 'config';

  @Input() serialNumbers;
  @Input() serviceDepartment;
  @Input() offlineOrder: boolean;

  @Output() onClose = new EventEmitter();
  @ViewChild('columnChooser') columnChooser;
  @ViewChild('serviceOrderGrid') serviceOrderGrid;
  USER: any;

  constructor(
    private commonService: CommonService
  ) {
    this.USER = commonService.USER;
  }

  ngOnInit(): void {
    this.USER = this.commonService.USER;
    this.userRoles = UserRoles;
    if (this.commonService.USER.role !== UserRoles.iscManager && this.commonService.USER.role !== UserRoles.IscSupervisor && this.commonService.USER.role !== UserRoles.isc) {
      this.getSerialNumbers(null, true);
    }
  }

  validate() {
    this.validationApplied = true;
    return this.model && this.srNumber;
  }

  getSerialNumbers(event, onInit?: boolean) {
    if (this.validate() || onInit) {
      const isIsc = this.commonService.USER.role === UserRoles.iscManager || this.commonService.USER.role === UserRoles.IscSupervisor || this.commonService.USER.role === UserRoles.isc;
      const api = isIsc ? 'EquipmentObject/GetISCSerialNumberSearchView' : 'EquipmentObject/GetSerialNumberSearchView';
      this.filterSettings = { type: 'Menu' };
      this.data = new DataManager({
        url: `${BASE_URL}${api}`,
        adaptor: new CustomAdaptor()
      });
      this.query = new Query().addParams('company', this.USER.company).addParams('serviceDepartment', this.serviceDepartment).addParams('offlineOrder', this.offlineOrder + '');
      if (isIsc) {
        this.query.addParams('serialSearch', 'true');
        this.model && this.query.addParams('model', this.model);
        this.srNumber && this.query.addParams('serialNumber', this.srNumber);
      }
    }
  }

  ngAfterViewInit(): void {
    this.setGrid();
  }

  setGrid() {
    const columns = [
      { field: 'serialNumber', headerText: 'Serial Number', visible: true, nonHideable: true, },
      { field: 'alternateSerialNumber', headerText: 'Alternate Serial Number', visible: true, nonHideable: true, },
      { field: 'model', headerText: 'Model Number', visible: true },
      { field: 'modelDescription', headerText: 'Model Description', visible: true },
      { field: 'configuration', headerText: 'Configuration', visible: true },
      { field: 'customerId', headerText: 'Customer Id', visible: true },
      { field: 'customerName', headerText: 'Customer Description', visible: true },
      { field: 'isNationalAccount', headerText: 'National Account', visible: false, showInColumnChooser: true, type: 'boolean' },
      { field: 'nationalAccount', headerText: 'National Account', visible: true, type: 'string', filter: { type: 'Menu' } },
      {
        field: 'Actions', headerText: 'Actions', showInColumnChooser: false, textAlign: 'Center', width: 120, allowFiltering: false, allowSorting: false,
        freeze: 'Right', allowReordering: false, commands: [
          {
            title: 'Proceed',
            buttonOption: {
              iconCss: `fas fa-arrow-right`,
              cssClass: `e-flat action-button`
            }
          }
        ]
      }
    ]
    this.mainGrid = {
      columns,
      toolbar: ['Search',
        { text: '', id: 'clear-filter', align: 'Right', prefixIcon: 'fas fa-filter', cssClass: `grid-filter-icon`, tooltipText: 'Clear all Filters' }
      ],
      pageSettings: { pageSize: 50 }
    };
  }

  toolbarClick(args: ClickEventArgs, grid: GridComponent) {
    if (args.item.id === 'clear-filter') {
      handleClearFilter(grid);
    }
  }

  onGridCreated() {
    updateFilterIcon({ requestType: 'filtering' }, this.serviceOrderGrid);
    bindInfiniteScrollPrefetch(this.serviceOrderGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
  }

  onDataBound() {
    bindInfiniteScrollPrefetch(this.serviceOrderGrid, () => this.totalRecords, 0, (count) => { this.totalRecords = count; });
  }

  onGridActionBegin(args, grid?) {
    gridActionBeginForFiltering(args, grid);
    if (args.requestType === 'infiniteScroll') setTimeout(() => this.serviceOrderGrid?.hideSpinner());
  }

  onGridActionComplete(args, grid) {
    if (args.requestType === 'infiniteScroll') setTimeout(() => grid?.hideSpinner());
    gridActionsForFiltering(args, grid);
    updateFilterIcon(args, grid);
    applyInfiniteGridTotalCount(grid, (count) => { this.totalRecords = count; }, args.requestType, args);
  }

  onCommandClick(args: any) {
    if (args.commandColumn.title === 'Proceed') {
      this.onClose.emit(args.rowData);
    }
  }

  close() {
    this.onClose.emit();
  }

  rowSelected(args) {
    this.selectedRowData = args.data;
  }

}
